import { inject } from '@angular/core';
import { CanMatchFn, Router } from '@angular/router';
import { catchError, map, Observable, of } from 'rxjs';
import { jwtDecode } from 'jwt-decode';
import { TokenService } from '../services/token.service';
import { UserService } from '../services/user.service';
import { AuthService } from '../services/auth.service';

/**
 * Whether a session is open: an access token that has not expired.
 * (TokenService.areTokensExpired cannot be used: its decoder is a stub that
 * always throws, so every token reads as expired.)
 */
const isSignedIn = (): boolean => {
  const token = inject(TokenService).getaccessToken();
  if (!token) return false;
  try {
    const { exp } = jwtDecode(token);
    return !exp || exp > Math.floor(Date.now() / 1000);
  } catch {
    return false;
  }
};

/** The signed-in user's role: the profile stored at sign-in, else the API */
const signedInRole = (): Observable<string | null> => {
  const saved = inject(UserService).getUserDataFromLocalStorage();
  if (saved) return of(saved.role);
  return inject(AuthService)
    .loadUser()
    .pipe(
      map((user) => user?.role ?? null),
      catchError(() => of(null))
    );
};

/**
 * The whole business area is closed to a signed-in customer (USER), who goes
 * home. A visitor reaches the landing and sign-up only by choosing to become
 * a partner inside the site, not by typing the address (or reloading it).
 */
export const businessAreaGuard: CanMatchFn = () => {
  const router = inject(Router);
  if (!isSignedIn()) {
    // `navigated` is false only on the first navigation of the page load
    return router.navigated ? true : router.createUrlTree(['/home']);
  }

  const authService = inject(AuthService);
  return signedInRole().pipe(
    map((role) =>
      authService.isBusinessRole(role) ? true : router.createUrlTree(['/home'])
    )
  );
};

/**
 * The partner back office is for business roles only: a visitor goes to the
 * partner landing to sign in, a customer goes home.
 */
export const businessRoleGuard: CanMatchFn = () => {
  const router = inject(Router);
  if (!isSignedIn()) return router.createUrlTree(['/business']);

  const authService = inject(AuthService);
  return signedInRole().pipe(
    map((role) =>
      authService.isBusinessRole(role) ? true : router.createUrlTree(['/home'])
    )
  );
};

